/**
 * ThemeTogglePill.tsx — Premium Dark/Light/Accessibility Toggle
 *
 * A pill-shaped toggle with 3 icons:
 *   ☀️ Light  |  🌙 Dark  |  ♿ High Contrast
 *
 * Active state uses the brand primary color.
 * Reads/writes from the nanostores theme + highContrast stores.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sun, Moon, Accessibility, ChevronDown } from 'lucide-react';

type ThemeMode = 'light' | 'dark' | 'accessibility';

interface ThemeTogglePillData {
  content?: { variant?: string; label?: string };
  variant?: string;
  settings?: {
    pillBackground?: string;
    pillBorder?: string;
    pillRadius?: string;
    pillPadding?: string;
    activeBackground?: string;
    activeColor?: string;
    activeShadow?: string;
  };
}
export const ThemeTogglePill: React.FC<{ data?: ThemeTogglePillData; context?: unknown }> = ({ data }) => {
  // PR G11 (2026-05-29) — React #418 hydration mismatch fix.
  // PRE-FIX: this useState used a lazy initializer that branched on
  // `typeof document === 'undefined'`. On the SERVER document is undefined
  // and the initializer returned 'light' → SSR markup highlighted the Sun
  // button. On the CLIENT document IS defined and the initializer read
  // `html.classList` which Layout.astro's blocking script had already
  // stamped with the persisted theme (e.g. 'dark') → React's first render
  // highlighted the Moon button. SSR text != hydrated text → React #418
  // ("Text content does not match server-rendered HTML"), and the entire
  // toggle subtree got recreated client-side.
  //
  // FIX: seed useState unconditionally with 'light' (the SSR value). The
  // useEffect below already syncs to the real html class on mount, so the
  // first hydration paint matches SSR exactly (no #418) and the swap to
  // the user's actual mode happens on the very next paint — no warning,
  // no DOM thrown away.
  //
  // Trade-off: a brief one-frame flicker if the persisted mode isn't
  // 'light'. The alternative is the React 418 throw + the entire
  // dropdown becoming unhandlable on first paint, which is much worse.
  // A long-term fix would have Layout.astro stamp the resolved mode into
  // a cookie that SSR reads — deferred until after the client demo.
  const [mode, setMode] = useState<ThemeMode>('light');

  // Variant: pill-toggle (default) | dropdown | inline-label
  const variantRaw = data?.content?.variant ?? data?.variant ?? 'pill-toggle';
  const variant: 'pill-toggle' | 'dropdown' | 'inline-label' =
    ['pill-toggle','dropdown','inline-label'].includes(variantRaw)
      ? variantRaw as 'pill-toggle' | 'dropdown' | 'inline-label'
      : 'pill-toggle';
  const inlineLabel = data?.content?.label ?? 'Mode';

  useEffect(() => {
    // Belt-and-braces: re-read the html class on mount in case the SSR
    // initial state above was 'light' (fallback) but the user had a
    // previous selection of 'dark' that Layout.astro's blocking script
    // applied to the html class before React hydration.
    const html = document.documentElement;
    let actual: ThemeMode = 'light';
    if (html.classList.contains('high-contrast')) actual = 'accessibility';
    else if (html.classList.contains('dark')) actual = 'dark';
    if (actual !== mode) setMode(actual);
    // mode is intentionally excluded from deps — this effect runs ONCE on
    // mount to sync, not on every mode change (which we drive ourselves
    // via applyMode).
    // (react-hooks/exhaustive-deps rule isn't loaded in the repo's eslint
    // config, so an explicit disable-comment would trigger
    // "Definition for rule … was not found". The single-run semantics
    // are documented above.)
  }, []);

  const applyMode = (newMode: ThemeMode) => {
    setMode(newMode);
    
    // Prevent mutating the Admin Editor's forced dark theme
    const inAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    if (inAdmin) return;

    const html = document.documentElement;

    if (newMode === 'accessibility') {
      html.classList.add('high-contrast');
      html.classList.remove('light', 'dark');
      html.setAttribute('data-theme', 'accessible');
      localStorage.setItem('high-contrast', JSON.stringify(true));
      localStorage.setItem('theme-mode', 'accessible');
      // Store standard dark for fallback gracefully
      localStorage.setItem('theme', JSON.stringify('dark')); 
    } else {
      // It's a base theme (light or dark)
      html.classList.remove('high-contrast');
      localStorage.setItem('high-contrast', JSON.stringify(false));
      html.setAttribute('data-theme', newMode);

      if (newMode === 'light') {
        html.classList.add('light');
        html.classList.remove('dark');
        localStorage.setItem('theme-mode', 'light');
        localStorage.setItem('theme', JSON.stringify('light'));
      } else {
        html.classList.add('dark');
        html.classList.remove('light');
        localStorage.setItem('theme-mode', 'dark');
        localStorage.setItem('theme', JSON.stringify('dark'));
      }
    }

    window.dispatchEvent(new CustomEvent('theme-change', { detail: { mode: newMode } }));
  };

  const buttons: { id: ThemeMode; icon: React.ReactNode; label: string }[] = [
    { id: 'light', icon: <Sun className="w-4 h-4" />, label: 'Light' },
    { id: 'dark', icon: <Moon className="w-4 h-4" />, label: 'Dark' },
    { id: 'accessibility', icon: <Accessibility className="w-4 h-4" />, label: 'Accessibility' },
  ];

  const {
    pillBackground = 'var(--color-header-icon-bg, rgba(255,255,255,0.06))',
    pillBorder = '1px solid var(--color-header-icon-border, var(--glass-border, rgba(255,255,255,0.18)))',
    pillRadius = 'var(--style-btn-radius, 9999px)',
    pillPadding = '2px', // p-0.5 is 2px
    // Active state uses brand secondary (chrome-accent role) — guarantees contrast
    // against the chrome bg, which is primary in mode-invariant chrome themes.
    activeBackground = 'var(--color-secondary, var(--accent-primary, #FB7C00))',
    activeColor = 'var(--color-header-promo-text, #18181B)',
    activeShadow = '0 0 0 1px var(--color-secondary, var(--accent-primary, #FB7C00)), 0 0 12px color-mix(in srgb, var(--color-secondary, var(--accent-primary, #FB7C00)) 30%, transparent)',
  } = data?.settings ?? {};

  // "All bordered pills" model (2026-05-30): every theme-mode segment is its
  // OWN .header-icon-btn pill, identical to wishlist / cart / quote / account /
  // search — so resting + hover are consistent across ALL header icons and are
  // driven entirely by the --color-header-icon-* preset tokens (a style/theme
  // preset reskins them all uniformly). The wrapper is now a transparent gap
  // container (no segmented-pill chrome). The ACTIVE segment fills using the
  // SAME hover tokens as every icon's hover chip, so "selected" reads as a
  // filled version of the shared hover state — no bespoke inline palette.
  // `pillBackground`/`pillBorder`/`pillRadius`/`activeBackground`/`activeColor`/
  // `activeShadow` are intentionally no longer applied inline; appearance comes
  // from .header-icon-btn + its tokens. (Settings retained for back-compat.)
  void pillBackground; void pillBorder; void pillRadius; void pillPadding;
  void activeBackground; void activeColor; void activeShadow;
  const pillRender = (
    <div
      className="inline-flex items-center gap-1"
      role="radiogroup"
      aria-label="Theme mode"
    >
      {buttons.map((btn) => {
        const isActive = mode === btn.id;
        return (
          <button
            key={btn.id}
            onClick={() => applyMode(btn.id)}
            // header-icon-btn drives resting bg/border/colour + hover for ALL
            // segments. Active adds a filled state via the shared hover tokens.
            className="relative flex items-center justify-center transition-all duration-200 header-icon-btn"
            // Active appearance (orange chip + auto-contrast icon) is driven by the
            // shared .header-icon-btn[aria-checked="true"] CSS rule — the SAME rule as
            // :hover — so active and hover are always identical and mode-aware. The
            // old inline active colour forced the icon dark (#18181B) while :hover
            // derived ~white in dark mode, giving the #36 "white-or-orange" mismatch.
            style={{ width: 36, height: 36 }}
            role="radio"
            aria-checked={isActive}
            aria-label={btn.label}
            title={btn.label}
          >
            {btn.icon}
          </button>
        );
      })}
    </div>
  );

  // Variant: pill-toggle — default. The three-segment pill alone.
  if (variant === 'pill-toggle') {
    return pillRender;
  }

  // Variant: inline-label — label preceding the pill. Label hides on mobile.
  if (variant === 'inline-label') {
    return (
      <div className="inline-flex items-center gap-2">
        <span
          className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: 'color-mix(in srgb, var(--color-header-icon, var(--text-secondary, #FFFFFF)) 65%, transparent)' }}
        >
          {inlineLabel}
        </span>
        {pillRender}
      </div>
    );
  }

  // Variant: dropdown — single icon button cycling open menu of modes.
  // Same source-of-truth (mode state) + applyMode() — just a different UI.
  return <ThemeToggleDropdown mode={mode} applyMode={applyMode} buttons={buttons} pillBackground={pillBackground} pillBorder={pillBorder} pillRadius={pillRadius} />;
};

/**
 * Dropdown variant: tap icon to open a small menu with 3 mode rows.
 * Closes on outside-click and Escape. Same accessibility contract as
 * the pill (each option still has role="radio").
 */
interface DropdownProps {
  mode: ThemeMode;
  applyMode: (m: ThemeMode) => void;
  buttons: { id: ThemeMode; icon: React.ReactNode; label: string }[];
  pillBackground: string;
  pillBorder: string;
  pillRadius: string;
}
const ThemeToggleDropdown: React.FC<DropdownProps> = ({ mode, applyMode, buttons, pillBackground, pillBorder, pillRadius }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const updateCoords = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    updateCoords();
    const handler = (e: MouseEvent | KeyboardEvent) => {
      if ('key' in e && (e).key === 'Escape') { setOpen(false); return; }
      if (
        'target' in e &&
        ref.current && !ref.current.contains(e.target as Node) &&
        menuRef.current && !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handler);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handler);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [open]);

  const activeBtn = buttons.find(b => b.id === mode) ?? buttons[1];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label="Theme mode"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center justify-center gap-1.5 transition-all duration-200"
        style={{
          width: 'auto',
          padding: '6px 10px',
          backgroundColor: pillBackground,
          border: pillBorder,
          borderRadius: pillRadius,
          color: 'var(--text-primary, #ffffff)',
        }}
      >
        {activeBtn.icon}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          role="menu"
          // shadow-xl removed — inline boxShadow below already drives this
          // via --style-modal-shadow with higher specificity.
          className="overflow-hidden"
          style={{
            position: 'fixed',
            top: coords.top,
            right: coords.right,
            zIndex: 99999,
            minWidth: 160,
            backgroundColor: 'var(--style-modal-bg, var(--surface-2))',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--style-modal-radius, var(--style-card-radius, 12px))',
            boxShadow: 'var(--style-modal-shadow, 0 12px 40px rgba(0,0,0,0.2))',
          }}
        >
          {buttons.map((btn) => {
            const isActive = mode === btn.id;
            return (
              <button
                key={btn.id}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => { applyMode(btn.id); setOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors"
                style={{
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)',
                  backgroundColor: isActive ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--accent-primary) 8%, transparent)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {btn.icon}
                <span>{btn.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ThemeTogglePill;
